import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { track, appUrlWithId } from '../../lib/analytics';

const TokenBadge = ({ token, icon }) => {
  const { darkMode } = useTheme();

  return (
    <div className={`flex items-center gap-2 rounded-full px-2 py-1.5 ${
      darkMode ? 'bg-[#232136]' : 'bg-gray-100'
    }`}>
      <img
        src={icon}
        alt={token}
        className={`w-7 h-7 rounded-full ${icon.includes('xlm') ? 'bg-white' : ''}`}
      />
      <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {token}
      </span>
      <svg className="w-4 h-4 text-[#A0A3C4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
};

export default function SwapWidget() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();

  return (
    <div className={`w-[90vw] lg:w-[28vw] min-w-[320px] max-w-[420px] rounded-2xl border-2 p-5 shadow-xl ${
      darkMode
        ? 'border-main/40 bg-[#181a25]'
        : 'border-main/30 bg-white'
    }`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('swapWidget.title')}
        </span>
        <button className={`p-2 rounded-lg transition-colors ${
          darkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
        }`}>
          <svg className={`w-5 h-5 ${darkMode ? 'text-[#A0A3C4]' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {/* Sell Panel */}
      <a
        href={appUrlWithId('https://app.soroswap.finance')}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          track('launch_app_clicked', {
            cta_id: 'swap_widget_swap',
            cta_text: t('swapWidget.sell'),
            section: 'hero',
            destination_url: 'https://app.soroswap.finance',
            destination_type: 'app'
          });
          track('cta_clicked', {
            cta_id: 'swap_widget_swap',
            cta_text: t('swapWidget.sell'),
            section: 'hero',
            destination_url: 'https://app.soroswap.finance',
            destination_type: 'app'
          });
        }}
        className={`block rounded-2xl border p-4 cursor-pointer transition-all hover:border-main/50 ${
          darkMode
            ? 'border-[#23243a] bg-[#10121A]'
            : 'border-gray-200 bg-gray-50'
        }`}
      >
        <span className="text-[#A0A3C4] text-sm font-medium">{t('swapWidget.sell')}</span>
        <div className="flex justify-between items-center mt-2">
          <span className={`text-3xl font-bold ${darkMode ? 'text-[#6d7179]' : 'text-gray-400'}`}>
            0
          </span>
          <TokenBadge token="XLM" icon="/images/tokens/xlm.png" />
        </div>
      </a>

      {/* Arrow Button */}
      <div className="relative flex justify-center -my-3 z-10">
        <div className={`rounded-full bg-[#CFFFD9] p-2 border-4 ${
          darkMode ? 'border-[#181a25]' : 'border-white'
        }`}>
          <svg className="w-5 h-5 text-[#232136]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

      {/* Buy Panel */}
      <a
        href={appUrlWithId('https://app.soroswap.finance')}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          track('launch_app_clicked', {
            cta_id: 'swap_widget_swap',
            cta_text: t('swapWidget.buy'),
            section: 'hero',
            destination_url: 'https://app.soroswap.finance',
            destination_type: 'app'
          });
          track('cta_clicked', {
            cta_id: 'swap_widget_swap',
            cta_text: t('swapWidget.buy'),
            section: 'hero',
            destination_url: 'https://app.soroswap.finance',
            destination_type: 'app'
          });
        }}
        className={`block rounded-2xl border p-4 cursor-pointer transition-all hover:border-main/50 ${
          darkMode
            ? 'border-[#2e294a] bg-transparent'
            : 'border-gray-200 bg-white'
        }`}
      >
        <span className="text-[#A0A3C4] text-sm font-medium">{t('swapWidget.buy')}</span>
        <div className="flex justify-between items-center mt-2">
          <span className={`text-3xl font-bold ${darkMode ? 'text-[#6d7179]' : 'text-gray-400'}`}>
            0
          </span>
          <TokenBadge token="USDC" icon="/images/tokens/usdc.svg" />
        </div>
      </a>

      {/* Exchange Rate */}
      <div className={`flex justify-between items-center mt-4 p-3 rounded-xl border ${
        darkMode ? 'border-[#23243a]' : 'border-gray-200'
      }`}>
        <span className="text-sm text-[#A0A3C4]">XLM / USDC</span>
        <svg className="w-4 h-4 text-[#A0A3C4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Launch App Button */}
      <a
        href={appUrlWithId('https://app.soroswap.finance')}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          track('launch_app_clicked', {
            cta_id: 'swap_widget_launch',
            cta_text: t('swapWidget.launchApp'),
            section: 'hero',
            destination_url: 'https://app.soroswap.finance',
            destination_type: 'app'
          });
          track('cta_clicked', {
            cta_id: 'swap_widget_launch',
            cta_text: t('swapWidget.launchApp'),
            section: 'hero',
            destination_url: 'https://app.soroswap.finance',
            destination_type: 'app'
          });
        }}
        className="mt-4 flex items-center justify-center gap-2 bg-main hover:bg-main2 rounded-2xl h-14 w-full font-bold text-white text-lg transition-all"
      >
        {t('swapWidget.launchApp')}
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    </div>
  );
}
